/**
 * Where to centre the canvas so a block is visible in the part of the canvas
 * that a settings pane at its right edge does not cover.
 */

/** Client rect of the canvas. */
export interface CanvasRect {
  left: number;
  width: number;
  height: number;
}

/** A block's bound in canvas units. */
export interface CanvasBlockBound {
  x: number;
  y: number;
  w: number;
  h: number;
  maxX: number;
  center: number[];
}

/** Free space kept between the block and the edge of the visible canvas. */
const CANVAS_MARGIN = 24;

/**
 * The canvas centre that shows `bound` at `zoom`. `coveredFrom` is the client
 * x from which a pane covers the canvas (null: nothing covers it). A block
 * that fits is centred in the free part; a wider or taller one shows its
 * right end (where new columns are added) and its top.
 */
export function canvasCenterFor(
  canvas: CanvasRect,
  bound: CanvasBlockBound,
  zoom: number,
  coveredFrom: number | null
): [number, number] {
  const canvasRight = canvas.left + canvas.width;
  const freeRight =
    coveredFrom !== null && coveredFrom > canvas.left
      ? Math.min(canvasRight, coveredFrom)
      : canvasRight;
  const freeWidth = freeRight - canvas.left - 2 * CANVAS_MARGIN;
  const freeHeight = canvas.height - 2 * CANVAS_MARGIN;
  // Client offset of a point from the canvas centre, in canvas units.
  const fromCenter = (offset: number) => offset / zoom;

  const x =
    bound.w * zoom <= freeWidth
      ? bound.center[0] + fromCenter((canvasRight - freeRight) / 2)
      : bound.maxX -
        fromCenter(freeRight - CANVAS_MARGIN - canvas.left - canvas.width / 2);
  const y =
    bound.h * zoom <= freeHeight
      ? bound.center[1]
      : bound.y + fromCenter(canvas.height / 2 - CANVAS_MARGIN);
  return [x, y];
}

/** A rectangle in canvas units. */
export interface CanvasArea {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where a block inside a note stands on the canvas. The note's bound only
 * says where the note is; the block is where the note renders it: its rect
 * on screen in canvas units, cut to the note (a collapsed note hides what
 * lies below its height). Null when the note shows nothing of the block.
 */
export function canvasAreaOfRendered(
  rect: { left: number; top: number; right: number; bottom: number },
  toCanvas: (point: [number, number]) => number[],
  note: CanvasArea
): CanvasArea | null {
  const [left, top] = toCanvas([rect.left, rect.top]);
  const [right, bottom] = toCanvas([rect.right, rect.bottom]);
  const x = Math.max(left, note.x);
  const y = Math.max(top, note.y);
  const maxX = Math.min(right, note.x + note.w);
  const maxY = Math.min(bottom, note.y + note.h);
  return maxX > x && maxY > y ? { x, y, w: maxX - x, h: maxY - y } : null;
}

/**
 * What lies on the canvas itself: the block, or the note that holds it. The
 * canvas holds the children of the root. A bound does not tell them apart: a
 * database inside a note still carries the bound of a block on the canvas.
 */
export function canvasBlockOf<T extends { parent: T | null }>(block: T): T {
  let current = block;
  while (current.parent?.parent) current = current.parent;
  return current;
}
